import { useMemo, useState } from "react"

import moment from "moment"
import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { Button, Divider, Dropdown, Form, Icon, Input, Message, Modal } from "semantic-ui-react"
import { CheckboxHeader } from "simplydo/react/components/CheckboxHeader"

import api from "api"
import { DateTimeInput } from "components/lib/DateInputs"
import { PaginationWithLimiter, Sticky } from "components/lib/UI"
import util from "utils/utils"

import { DeleteNameContainer, DeleteNameItem } from "./Styles"

const Pagination = ({ limit, page, total, updateFilters, limitValues }) => (
  <Sticky
    bottom={15}
    style={{
      textAlign: "center",
      zIndex: 10,
      display: "flex",
      justifyContent: "center",
      marginTop: 10,
    }}
  >
    <PaginationWithLimiter
      limit={limit}
      page={page}
      total={total}
      limitValues={limitValues}
      updateLimit={(value) => updateFilters({ ideaLimit: value })}
      updatePage={(value) => updateFilters({ ideaPage: value })}
    />
  </Sticky>
)

const IdeaDeletionModal = ({ challengeId, ideasToDelete = [], setIdeasToDelete, setSelectedIdeas }) => {
  const [deleteConfirm, setDeleteConfirm] = useState("")
  const { t } = useTranslation()

  // Get all child ideas from all ideas to delete by flattening
  const allChildIdeas = useMemo(() => {
    const allChildren = []
    ideasToDelete.forEach((idea) => {
      if (idea.ownerChildren) {
        allChildren.push(...idea.ownerChildren)
      }
    })
    return allChildren
  }, [ideasToDelete])

  return (
    <Modal
      mountNode={document.getElementById("semantic-modal-mount-node")}
      open={!!ideasToDelete.length}
      onClose={() => setIdeasToDelete([])}
    >
      <Modal.Header>Delete {t("generic.ideas")}</Modal.Header>
      <Modal.Content scrolling>
        <p>You are choosing to delete the following {t("generic.ideas")}:</p>
        <ul>
          {ideasToDelete.map((idea) => (
            <li key={idea._id}>
              {idea.children ? <Icon name="sitemap" /> : null}
              <b>
                {idea.name}
                {idea.owner ? ` - ${idea.owner.profile.fullName}` : ""}
              </b>
            </li>
          ))}
        </ul>
        {allChildIdeas.length ? (
          <>
            <p>
              The following child {t("generic.ideas")} will be removed from the deleted merged {t("generic.ideas")}, and
              returned to the {t("generic.challenge")} as individual {t("generic.ideas")}.
            </p>
            <ul>
              {allChildIdeas.map((idea) => (
                <li key={idea._id}>
                  <b>{idea.name}</b>
                </li>
              ))}
            </ul>
            <p>
              Any comments left on the merged {t("generic.ideas")} will be deleted, but any comments left on the
              original {t("generic.ideas")} will be returned to them.
            </p>
          </>
        ) : null}
        <Message error>
          This action is irreversible. These {t("generic.ideas")} will be permanently deleted, and cannot be recovered.
          Please make sure this action is correct, and double check the {t("generic.ideas")} you have chosen.
        </Message>
        <DeleteNameContainer>
          <DeleteNameItem>
            <p>Type the word 'DELETE' below to confirm you understand this.</p>
            <Input placeholder="DELETE" value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)} />
          </DeleteNameItem>
        </DeleteNameContainer>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={() => setIdeasToDelete([])}>Cancel</Button>
        <Button
          disabled={deleteConfirm !== "DELETE"}
          color="red"
          onClick={() => {
            util
              .confirm(
                `Delete ${t("generic.ideas")}`,
                `Are you absolutely sure you want to permanently delete these ${t("generic.ideas")}?`,
              )
              .then(() => {
                api.ideas.bulkDelete(
                  ideasToDelete.map((i) => i._id),
                  () => {
                    api.queryClient.setQueriesData(
                      { queryKey: ["challenges", challengeId, "ideas", "fetch"] },
                      (oldData) => ({
                        ...oldData,
                        ideas: oldData.ideas.filter((i) => ideasToDelete.map((i) => i._id).indexOf(i._id) === -1),
                      }),
                    )
                    // Invalidate challenge query to update idea count
                    api.queryClient.invalidateQueries({
                      queryKey: ["challenges", challengeId],
                      exact: true,
                    })
                    // Invalidate ideas query to reload totals and impact lane measures
                    api.queryClient.invalidateQueries({
                      queryKey: ["challenges", challengeId, "ideas", "fetch"],
                    })
                    api.queryClient.invalidateQueries({
                      queryKey: ["challenges", challengeId, "ideas", "me"],
                    })
                    setSelectedIdeas((prev) =>
                      prev.filter((p) => ideasToDelete.map((i) => i._id).indexOf(p._id) === -1),
                    )
                    setIdeasToDelete([])
                    toast.success(`${t("common:capitalise", { key: "generic.ideas" })} deleted`)
                  },
                  (err) => toast.error(err.message),
                )
                setDeleteConfirm("")
              })
              .catch(() => {
                setDeleteConfirm("")
              })
          }}
        >
          Delete
        </Button>
      </Modal.Actions>
    </Modal>
  )
}

const IdeaDeadlineModal = ({ challengeId, deadlineIdeas = [], setDeadlineIdeas }) => {
  const { t } = useTranslation()
  const [note, setNote] = useState("")
  const [shouldUnsubmit, setShouldUnsubmit] = useState(true)
  const [notifyAuthors, setNotifyAuthors] = useState(true)
  const now = new Date()
  const defaultDeadline = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)
  const [deadline, setDeadline] = useState(defaultDeadline)

  return (
    <Modal
      mountNode={document.getElementById("semantic-modal-mount-node")}
      open={!!deadlineIdeas.length}
      onClose={() => setDeadlineIdeas([])}
    >
      <Modal.Header>Set {t("generic.idea")} deadline</Modal.Header>
      <Modal.Content>
        <p>You are setting a deadline for the following {t("generic.ideas")}:</p>
        <ul>
          {deadlineIdeas.map((idea) => (
            <li key={idea._id}>
              <b>{idea.name}</b>
              {idea.owner ? ` by ${idea.owner.profile.fullName}` : ""}
            </li>
          ))}
        </ul>
        <Divider />
        <b>Deadline</b>
        <p>
          The {util.pluralise(deadlineIdeas, t("generic.idea"), t("generic.ideas"), false)} will be submissible until
          the chosen date, irrespective of the deadline chosen for this {t("generic.challenge")}.
        </p>
        <DateTimeInput
          value={deadline}
          onChange={(_e, { value }) => setDeadline(moment(value, "DD-MM-YYYY HH:mm").toISOString())}
          // minDate={now}
        />
        <Divider hidden />
        <b>Notes</b>
        <p>
          The context of these notes will be sent to the {t("generic.idea")} authors once you have set the new deadline.
          You can choose to provide details for the authors regarding their submission or any other relevant
          information.
        </p>
        <Form>
          <Form.TextArea placeholder="Add a note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
        </Form>
        <Divider hidden />
        <CheckboxHeader
          as="h5"
          header={`Unsubmit ${t("generic.ideas")}`}
          description={`If ${t("generic.ideas")} are submitted they will return to their draft format.`}
          checked={shouldUnsubmit}
          onChange={() => setShouldUnsubmit((prev) => !prev)}
        />
        <CheckboxHeader
          as="h5"
          header="Notify authors"
          description="Authors will be notified of the new deadline and any notes you have added. This will also include the changed submission status (if applicable)."
          checked={notifyAuthors}
          onChange={() => setNotifyAuthors((prev) => !prev)}
        />
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={() => setDeadlineIdeas([])}>Cancel</Button>
        <Button
          basic
          onClick={() => {
            util
              .confirm("Remove deadline", "Are you sure you want to remove the deadline from these ideas?")
              .then(() => {
                api.ideas.removeDeadline(
                  deadlineIdeas.map((i) => i._id),
                  ({ ideas }) => {
                    toast.success("Deadlines removed")
                    setDeadlineIdeas([])
                    api.queryClient.setQueriesData(
                      { queryKey: ["challenges", challengeId, "ideas", "fetch"] },
                      (oldData) => ({
                        ...oldData,
                        ideas: oldData.ideas.map((i) => ideas.find((u) => u._id === i._id) ?? i),
                      }),
                    )
                    for (const idea of deadlineIdeas) {
                      api.queryClient.invalidateQueries({
                        queryKey: ["ideas", idea._id],
                      })
                    }
                  },
                  (err) => toast.error(err.message),
                )
              })
              .catch(() => {})
          }}
        >
          Remove deadline
        </Button>
        <Button
          primary
          onClick={() => {
            const deadlineIdeasIds = deadlineIdeas.map((i) => i._id)
            api.ideas.setDeadline(
              {
                ideaIds: deadlineIdeasIds,
                deadlineData: {
                  deadline: moment(deadline).toISOString(),
                  note,
                },
                shouldUnsubmit,
                notifyAuthors,
              },
              () => {
                setDeadlineIdeas([])
                toast.success("Deadline set")
                api.queryClient.setQueriesData(
                  { queryKey: ["challenges", challengeId, "ideas", "fetch"] },
                  (oldData) => ({
                    ...oldData,
                    ideas: oldData.ideas.map((i) => {
                      if (deadlineIdeasIds.indexOf(i._id) === -1) return i
                      return {
                        ...i,
                        submissionDeadline: {
                          deadline: moment(deadline).toISOString(),
                          deadlineNote: note,
                        },
                        isSubmitted: shouldUnsubmit ? false : i.isSubmitted,
                        submittedBy: shouldUnsubmit ? null : i.submittedBy,
                        submittedAt: shouldUnsubmit ? null : i.submittedAt,
                      }
                    }),
                  }),
                )
                for (const idea of deadlineIdeas) {
                  api.queryClient.invalidateQueries({
                    queryKey: ["ideas", idea._id],
                  })
                }
              },
              (err) => toast.error(err.message),
            )
          }}
        >
          Set deadline
        </Button>
      </Modal.Actions>
    </Modal>
  )
}

const IdeaManagementDeadlineModal = ({
  managementDeadlineIdeas = [],
  setManagementDeadlineIdeas,
  updateIdeaProjectDeadlineMulti,
}) => {
  const { t } = useTranslation()
  const now = new Date()
  const defaultDeadline = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)
  const [managementDeadline, setManagementDeadline] = useState(defaultDeadline)

  return (
    <Modal
      mountNode={document.getElementById("semantic-modal-mount-node")}
      open={!!managementDeadlineIdeas.length}
      onClose={() => setManagementDeadlineIdeas([])}
    >
      <Modal.Header>Set {t("generic.idea")} management deadline</Modal.Header>
      <Modal.Content>
        {" "}
        <div style={{ marginBottom: "25px" }}>
          <p>You are setting a management deadline for the following {t("generic.ideas")}:</p>
          <ul>
            {managementDeadlineIdeas.map((idea) => (
              <li key={idea._id}>
                <b>{idea.name}</b>
                {idea.owner ? ` by ${idea.owner.profile.fullName}` : ""}
              </li>
            ))}
          </ul>
        </div>
        <Divider />
        <div>
          <b>Deadline</b>
          <p>Set a project deadline for all selected {t("generic.ideas")}</p>
          <DateTimeInput
            value={managementDeadline}
            onChange={(_e, { value }) => setManagementDeadline(moment(value, "DD-MM-YYYY HH:mm").toISOString())}
            minDate={now}
          />
        </div>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={() => setManagementDeadlineIdeas([])}>Cancel</Button>
        <Button
          primary
          onClick={() => {
            updateIdeaProjectDeadlineMulti(managementDeadline)
            setManagementDeadlineIdeas([])
          }}
        >
          Set deadline
        </Button>
      </Modal.Actions>
    </Modal>
  )
}

const IdeaMoveToLaneModal = ({
  moveLaneIdeas = [],
  setMoveLaneIdeas,
  allChallengeProjectLanes = [],
  checkUpdateProjectLaneMulti,
  challenge,
}) => {
  const { t } = useTranslation()
  const [selectedLaneId, setSelectedLaneId] = useState(challenge?.projectBoard?.defaultLane?._id ?? "default")

  return (
    <Modal
      mountNode={document.getElementById("semantic-modal-mount-node")}
      open={!!moveLaneIdeas.length}
      onClose={() => setMoveLaneIdeas([])}
    >
      <Modal.Header>
        Move {util.pluralise(moveLaneIdeas.length, t("generic.idea"), t("generic.ideas"), false)} to project lane
      </Modal.Header>

      <Modal.Content>
        <p>
          You are moving the following{" "}
          {util.pluralise(moveLaneIdeas.length, t("generic.idea"), t("generic.ideas"), false)} to a new project lane:
        </p>
        <ul>
          {moveLaneIdeas.map((idea) => (
            <li key={idea._id}>
              <b>{idea.name}</b>
              {idea.owner ? ` by ${idea.owner.profile.fullName}` : ""}
            </li>
          ))}
        </ul>

        <Divider />

        <b>Select lane</b>
        <p>
          Select the lane where these{" "}
          {util.pluralise(moveLaneIdeas.length, t("generic.idea"), t("generic.ideas"), false)} will be moved.
        </p>
        <Dropdown
          selection
          fluid
          value={selectedLaneId}
          options={[
            {
              key: "default",
              value: "default",
              text: challenge?.projectBoard?.defaultLane?.name ?? "Default",
            },
            ...allChallengeProjectLanes.map((lane) => ({
              key: lane._id,
              value: lane._id,
              text: lane.name,
            })),
          ]}
          onChange={(_e, { value }) => setSelectedLaneId(value)}
        />
      </Modal.Content>

      <Modal.Actions>
        <Button onClick={() => setMoveLaneIdeas([])}>Cancel</Button>
        <Button
          primary
          onClick={() => {
            checkUpdateProjectLaneMulti(
              moveLaneIdeas.map((idea) => idea._id),
              selectedLaneId,
            )
            setMoveLaneIdeas([])
          }}
        >
          Move ideas
        </Button>
      </Modal.Actions>
    </Modal>
  )
}

export { IdeaDeadlineModal, IdeaDeletionModal, IdeaManagementDeadlineModal, IdeaMoveToLaneModal, Pagination }
